import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Shift, ShiftRosterCopyModel, ShiftRosterEntry, ShiftRosterReplaceModel, ShiftSaveModel } from '../model/shift';

const shiftsPath = '/api/shifts';
const rosterPath = '/api/shift-roster';

/**
 * HTTP client for shift definitions and the duty roster. Reads need read:roster (granted to every staff
 * role); writes need write:roster (design leads and MoonSmile admins).
 */
@Injectable({ providedIn: 'root' })
export class ShiftClient {
    private readonly _httpClient = inject(HttpClient);

    list(isActive?: boolean | null, search?: string | null): Observable<Shift[]> {
        let params = new HttpParams();
        if (isActive != null) {
            params = params.set('isActive', String(isActive));
        }
        if (search != null && search.length > 0) {
            params = params.set('search', search);
        }
        return this._httpClient.get<Shift[]>(shiftsPath, { params });
    }

    getById(id: number): Observable<Shift> {
        return this._httpClient.get<Shift>(`${shiftsPath}/${id}`);
    }

    create(model: ShiftSaveModel): Observable<Shift> {
        return this._httpClient.post<Shift>(shiftsPath, model);
    }

    update(id: number, model: ShiftSaveModel): Observable<Shift> {
        return this._httpClient.put<Shift>(`${shiftsPath}/${id}`, model);
    }

    setActive(id: number, isActive: boolean): Observable<Shift> {
        return this._httpClient.patch<Shift>(`${shiftsPath}/${id}/active`, isActive);
    }

    delete(id: number): Observable<void> {
        return this._httpClient.delete<void>(`${shiftsPath}/${id}`);
    }

    /** Roster rows for a date range, optionally narrowed to one shift. */
    listRoster(from: string, to: string, shiftId?: number | null): Observable<ShiftRosterEntry[]> {
        let params = new HttpParams().set('from', from).set('to', to);
        if (shiftId != null) {
            params = params.set('shiftId', String(shiftId));
        }
        return this._httpClient.get<ShiftRosterEntry[]>(rosterPath, { params });
    }

    /** Who is on duty at this moment, per the lab-local clock. */
    listOnDuty(): Observable<ShiftRosterEntry[]> {
        return this._httpClient.get<ShiftRosterEntry[]>(`${rosterPath}/on-duty`);
    }

    /** Replaces the roster for one shift over a date range with exactly the supplied entries. */
    replaceRoster(model: ShiftRosterReplaceModel): Observable<ShiftRosterEntry[]> {
        return this._httpClient.put<ShiftRosterEntry[]>(rosterPath, model);
    }

    /** Repeats an existing roster block forward, for monthly pre-assignment. */
    copyRoster(model: ShiftRosterCopyModel): Observable<ShiftRosterEntry[]> {
        return this._httpClient.post<ShiftRosterEntry[]>(`${rosterPath}/copy`, model);
    }
}
